import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import { Modal } from '@proaxiv/ui'
import {
  useGetNotificationsQuery,
  useMarkReadMutation,
  useDeleteNotificationsMutation,
  type GetNotificationsResponse,
  type NotificationSummary,
} from '@proaxiv/server-api'
import { Button } from '../components/Button'
import { ConfirmationModal } from '../components/ConfirmationModal'
import { formatNotification } from '../utils/formatNotification'

const PAGE_SIZE = 20

// ── Icons ──────────────────────────────────────────────────────────────────────

const BackArrow = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    aria-hidden="true"
  >
    <path d="M19 12H5M12 5l-7 7 7 7" />
  </svg>
)

const CheckIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.25"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M20 6L9 17l-5-5" />
  </svg>
)

const TrashIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0-1 14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2L4 6" />
  </svg>
)

const CloseIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
)

/** Formats an ISO timestamp for the notifications list/detail view. */
function formatWhen(iso: string, locale: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return new Intl.DateTimeFormat(locale, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(d)
}

/**
 * Full notifications inbox — every notification, read and unread. Supports selecting one or many
 * rows to mark-as-read/delete in bulk, per-row actions, and a detail view (opened by clicking a
 * row) that shows the full message and a button to follow the notification's link. Opening the
 * detail view marks the notification read. See #156.
 */
export function NotificationsPage() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const [page, setPage] = useState(0)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [detail, setDetail] = useState<NotificationSummary | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<string[] | null>(null)

  const { data: rawData, isLoading } = useGetNotificationsQuery({ page, size: PAGE_SIZE })
  const [markRead] = useMarkReadMutation()
  const [deleteNotifications] = useDeleteNotificationsMutation()

  const data = rawData as GetNotificationsResponse | undefined
  const notifications = data?.notifications ?? []
  const total = data?.total ?? 0
  const hasNextPage = (page + 1) * PAGE_SIZE < total

  const selectedCount = selectedIds.size
  const allOnPageSelected =
    notifications.length > 0 && notifications.every((n) => selectedIds.has(n.notificationId))
  const detailCopy = detail ? formatNotification(detail, t) : null

  function toggleRow(id: string, checked: boolean) {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (checked) next.add(id)
      else next.delete(id)
      return next
    })
  }

  function toggleAllOnPage(checked: boolean) {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      for (const n of notifications) {
        if (checked) next.add(n.notificationId)
        else next.delete(n.notificationId)
      }
      return next
    })
  }

  async function handleOpenDetail(notification: NotificationSummary) {
    setDetail(notification)
    if (!notification.read) {
      try {
        await markRead({ notificationId: notification.notificationId }).unwrap()
      } catch {
        // Best-effort — the detail view still opens even if the read-flag update fails.
      }
    }
  }

  async function handleMarkOneRead(id: string) {
    try {
      await markRead({ notificationId: id }).unwrap()
    } catch {
      // Best-effort.
    }
  }

  async function handleMarkSelectedRead() {
    const ids = Array.from(selectedIds)
    await Promise.all(
      ids.map((id) =>
        markRead({ notificationId: id })
          .unwrap()
          .catch(() => {})
      )
    )
    setSelectedIds(new Set())
  }

  async function handleConfirmDelete() {
    if (!confirmDelete) return
    try {
      await deleteNotifications({
        deleteNotificationsBody: { notificationIds: confirmDelete },
      }).unwrap()
    } catch {
      // Best-effort — the list simply won't update if this fails.
    }
    setSelectedIds((prev) => {
      const next = new Set(prev)
      for (const id of confirmDelete) next.delete(id)
      return next
    })
    setConfirmDelete(null)
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6 px-4 py-6 md:px-6 md:py-8">
      <Link
        to="/"
        className="text-pax-gray hover:text-pax-blue flex items-center gap-1.5 text-sm transition-colors focus-visible:outline-none"
      >
        <BackArrow />
        {t('nav_home')}
      </Link>

      <div className="flex flex-col gap-1">
        <h1 className="font-display text-pax-black text-2xl font-bold tracking-tight md:text-3xl">
          {t('header_notifications')}
        </h1>
        <p className="text-pax-gray text-sm">{t('notif_page_subtitle')}</p>
      </div>

      {selectedCount > 0 && (
        <div className="border-pax-purple/30 bg-pax-light-purple/40 flex flex-wrap items-center gap-3 rounded-xl border px-4 py-3">
          <span className="text-pax-gray text-xs font-medium">
            {t('n_selected', { count: selectedCount })}
          </span>
          <Button
            variant="ghost"
            size="sm"
            leftIcon={<CheckIcon />}
            onClick={() => void handleMarkSelectedRead()}
          >
            {t('notif_mark_selected_read')}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            leftIcon={<TrashIcon />}
            onClick={() => setConfirmDelete(Array.from(selectedIds))}
          >
            {t('notif_delete_selected')}
          </Button>
        </div>
      )}

      <div className="border-pax-purple/20 overflow-hidden rounded-2xl border bg-white">
        {isLoading && (
          <p className="text-pax-gray px-4 py-8 text-center text-sm">{t('notif_loading')}</p>
        )}
        {!isLoading && notifications.length === 0 && (
          <p className="text-pax-gray px-4 py-8 text-center text-sm">{t('notif_empty')}</p>
        )}
        {!isLoading && notifications.length > 0 && (
          <>
            <div className="border-pax-purple/10 flex items-center gap-3 border-b px-4 py-2.5">
              <input
                type="checkbox"
                aria-label={t('select_all')}
                checked={allOnPageSelected}
                onChange={(e) => toggleAllOnPage(e.target.checked)}
                className="accent-pax-blue h-4 w-4 cursor-pointer"
              />
              <span className="text-pax-gray text-xs font-medium">{t('select_all')}</span>
            </div>
            <ul>
              {notifications.map((n, i) => {
                const copy = formatNotification(n, t)
                return (
                  <li
                    key={n.notificationId}
                    className={[
                      'border-pax-purple/10 flex items-start gap-3 border-b px-4 py-3 last:border-b-0',
                      // Zebra-striped rows make it easier to visually track a row across its full
                      // width; the unread tint takes priority when both would otherwise apply.
                      !n.read ? 'bg-pax-light-purple/25' : i % 2 === 1 ? 'bg-pax-gray/5' : '',
                    ].join(' ')}
                  >
                    <input
                      type="checkbox"
                      aria-label={copy.title}
                      checked={selectedIds.has(n.notificationId)}
                      onChange={(e) => toggleRow(n.notificationId, e.target.checked)}
                      className="accent-pax-blue mt-1 h-4 w-4 cursor-pointer"
                    />
                    <button
                      type="button"
                      onClick={() => void handleOpenDetail(n)}
                      className="min-w-0 flex-1 cursor-pointer text-left"
                    >
                      <div className="flex items-center gap-2">
                        {!n.read && (
                          <span
                            aria-label={t('notif_unread')}
                            className="bg-pax-blue inline-block h-2 w-2 flex-shrink-0 rounded-full"
                          />
                        )}
                        <span
                          className={[
                            'text-pax-black text-sm',
                            !n.read ? 'font-semibold' : 'font-medium',
                          ].join(' ')}
                        >
                          {copy.title}
                        </span>
                      </div>
                      <p className="text-pax-gray mt-0.5 line-clamp-1 text-sm">{copy.message}</p>
                      <p className="text-pax-gray/70 mt-0.5 text-xs">
                        {formatWhen(n.createdAt, i18n.language)}
                      </p>
                    </button>
                    <div className="flex flex-shrink-0 items-center gap-1">
                      {!n.read && (
                        <Button
                          type="button"
                          variant="unstyled"
                          aria-label={t('notif_mark_read')}
                          title={t('notif_mark_read')}
                          onClick={() => void handleMarkOneRead(n.notificationId)}
                          className="text-pax-gray hover:bg-pax-light-purple hover:text-pax-blue flex h-8 w-8 items-center justify-center rounded-lg transition-colors"
                        >
                          <CheckIcon />
                        </Button>
                      )}
                      <Button
                        type="button"
                        variant="unstyled"
                        aria-label={t('notif_delete')}
                        title={t('notif_delete')}
                        onClick={() => setConfirmDelete([n.notificationId])}
                        className="text-pax-gray hover:bg-pax-red/10 hover:text-pax-red flex h-8 w-8 items-center justify-center rounded-lg transition-colors"
                      >
                        <TrashIcon />
                      </Button>
                    </div>
                  </li>
                )
              })}
            </ul>
          </>
        )}
      </div>

      {total > PAGE_SIZE && (
        <div className="flex items-center justify-center gap-3">
          <Button
            variant="secondary"
            size="sm"
            disabled={page === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
          >
            {t('btn_previous')}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            disabled={!hasNextPage}
            onClick={() => setPage((p) => p + 1)}
          >
            {t('btn_next')}
          </Button>
        </div>
      )}

      {/* Detail view */}
      {detail && detailCopy && (
        <Modal labelledBy="notif-detail-title">
          <div className="border-pax-purple/50 relative w-full max-w-md rounded-2xl border bg-white p-6 shadow-2xl ring-1 ring-black/5">
            <button
              type="button"
              onClick={() => setDetail(null)}
              className="text-pax-gray hover:text-pax-black absolute top-4 right-4 cursor-pointer rounded-lg p-1 transition-colors focus-visible:outline-none"
              aria-label={t('btn_close')}
            >
              <CloseIcon />
            </button>

            <h2 id="notif-detail-title" className="text-pax-black mb-1 pr-8 text-lg font-bold">
              {detailCopy.title}
            </h2>
            <p className="text-pax-gray/70 mb-4 text-xs">
              {formatWhen(detail.createdAt, i18n.language)}
            </p>
            <p className="text-pax-black mb-6 text-sm whitespace-pre-wrap">{detailCopy.message}</p>

            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:gap-3">
              <Button variant="secondary" onClick={() => setDetail(null)}>
                {t('btn_close')}
              </Button>
              {detail.link && (
                <Button
                  variant="primary"
                  onClick={() => {
                    const link = detail.link
                    setDetail(null)
                    if (link) navigate(link)
                  }}
                >
                  {t('notif_follow_link')}
                </Button>
              )}
            </div>
          </div>
        </Modal>
      )}

      {/* Confirm delete */}
      {confirmDelete !== null && (
        <ConfirmationModal
          title={t('notif_delete_confirm', { count: confirmDelete.length })}
          notify={false}
          showNotifyCheckbox={false}
          onConfirm={() => void handleConfirmDelete()}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
    </div>
  )
}
